import type { Message, ModelRef } from "./opencodeTypes"
import {
  applyDelta,
  applyPart,
  lastAssistant,
  messageFromInfo,
  type WireMessageInfo,
  type WirePart,
} from "./v1"

// Client-side projection of the agent's v1 event bus into messages.
//
// The bus carries a message's `info` and each of its parts as separate events,
// in that order, with streamed text as deltas on top. This folds them into the
// message list the components render. The builders themselves live in lib/v1.ts,
// where history uses the same ones — so a transcript assembled live and one
// fetched on reload cannot disagree.
//
// One stream, GET /event, carries everything: it is instance-wide, so every
// session on the container shares it and the chat filters by sessionID. The
// frame on the wire is `{ id, type, properties }`; OpenCodeApi.subscribeSSE
// hands it over as `{ id, type, data }` so this file and its tests read one
// shape. Definitions: agent/packages/schema/src/v1/session.ts (message.*,
// session.error), session-status-event.ts (session.status, session.idle).

export interface SessionEvent {
  id?: string
  type: string
  data?: Record<string, any>
}

// The session an event belongs to. Status events carry it at the top level;
// message and part events carry it inside the record they are about.
export function eventSessionId(event: SessionEvent): string | undefined {
  const data = event.data ?? {}
  return data["sessionID"] ?? data["info"]?.sessionID ?? data["part"]?.sessionID
}

export function isPlaceholderSessionTitle(title: string | undefined): boolean {
  return !title || title === "New session" || /^New session - \d{4}-\d{2}-\d{2}T/.test(title)
}

/** The agent already publishes its first-prompt title on session.updated. */
export function generatedSessionTitle(event: SessionEvent, rootSessionId: string): string | undefined {
  if (event.type !== "session.updated") return undefined
  const info = event.data?.["info"] as { id?: string; sessionID?: string; parentID?: string; title?: string } | undefined
  if (!info || (info.id ?? info.sessionID) !== rootSessionId || info.parentID) return undefined
  const title = info.title?.trim()
  return isPlaceholderSessionTitle(title) ? undefined : title
}

// Errors arrive as { name, data: { message } } or { type, message }, never as
// strings. Rendering one straight into JSX throws React error #31 ("Objects
// are not valid as a React child"), which the ErrorBoundary turns into a blank
// "Something went wrong" page — so a single failed turn anywhere in a session's
// history made the whole workspace look broken. Everything that displays an
// error goes through here.
export function errorText(error: unknown): string {
  if (typeof error === "string") return error
  if (error && typeof error === "object") {
    const record = error as { message?: unknown; data?: { message?: unknown }; name?: unknown }
    if (typeof record.message === "string") return record.message
    if (typeof record.data?.message === "string") return record.data.message
    if (typeof record.name === "string") return record.name
  }
  return JSON.stringify(error)
}

// Appends unless the id is already present, so replayed history and live events
// can be applied to the same array without producing duplicates.
function upsertMessage(messages: Message[], message: Message): Message[] {
  const index = messages.findIndex((m) => m.id === message.id)
  if (index === -1) return [...messages, message]
  const next = messages.slice()
  next[index] = message
  return next
}

// Replaces the message with `id`, leaving every other message alone. A miss is
// ignored rather than throwing: the bus can deliver a part for a message this
// client never loaded (another tab's session, or history beyond the first
// page).
function updateMessage(messages: Message[], id: string | undefined, recipe: (draft: Message) => Message): Message[] {
  if (!id) return messages
  const index = messages.findIndex((m) => m.id === id)
  const current = messages[index]
  if (index === -1 || !current) return messages
  const next = messages.slice()
  next[index] = recipe(current)
  return next
}

export function applySessionEvent(messages: Message[], event: SessionEvent): Message[] {
  const data = event.data ?? {}

  switch (event.type) {
    case "message.updated": {
      const info = data["info"] as WireMessageInfo | undefined
      if (!info?.id) return messages
      const previous = messages.find((m) => m.id === info.id)
      return upsertMessage(messages, messageFromInfo(info, previous))
    }

    case "message.part.updated": {
      const part = data["part"] as WirePart | undefined
      if (!part?.messageID) return messages
      return updateMessage(messages, part.messageID, (draft) => applyPart(draft, part))
    }

    // The event that makes the answer appear as it is written.
    case "message.part.delta":
      return updateMessage(messages, data["messageID"], (draft) =>
        applyDelta(draft, data["partID"], data["field"] ?? "text", data["delta"] ?? ""),
      )

    case "message.removed":
      return messages.filter((m) => m.id !== data["messageID"])

    // A turn that died before its message could say so. Attached to the reply
    // it interrupted, so the failure shows where the reader was looking rather
    // than nowhere.
    case "session.error": {
      const last = lastAssistant(messages)
      if (!last || last.error != null) return messages
      return updateMessage(messages, last.id, (draft) =>
        draft.type === "assistant" ? { ...draft, finish: "error", error: data["error"] } : draft,
      )
    }

    default:
      return messages
  }
}

// The model the session is on, as far as the transcript knows: what answered
// last. Read from the messages rather than kept in state, so a switch made
// here, one made in another tab, and a reload all agree. Undefined means
// nothing has answered yet and the session's own default is the answer.
export function lastSwitchedModel(messages: Message[]): ModelRef | undefined {
  return lastAssistant(messages)?.model
}

// Same idea for the agent (Build / Plan).
export function lastSwitchedAgent(messages: Message[]): string | undefined {
  return lastAssistant(messages)?.agent
}

// Whether an event means the agent is still working. Drives the "thinking"
// indicator without a second request. An assistant message exists from the
// moment the turn starts and gains `time.completed` when it settles; a step
// starting or finishing says the same thing at a finer grain.
export function isTurnActive(event: SessionEvent): boolean | undefined {
  const data = event.data ?? {}
  if (event.type === "message.updated") {
    const info = data["info"] as WireMessageInfo | undefined
    if (info?.role !== "assistant") return undefined
    return info.time?.completed === undefined
  }
  if (event.type === "message.part.updated") {
    const type = (data["part"] as WirePart | undefined)?.type
    if (type === "step-start") return true
    if (type === "step-finish") return false
  }
  return undefined
}

// A model step has ended: usage for it has been reported, and the ledger will
// have a row for it shortly. What the cost refresh listens for.
export function isStepEnded(event: SessionEvent): boolean {
  const data = event.data ?? {}
  if (event.type === "message.part.updated") return (data["part"] as WirePart | undefined)?.type === "step-finish"
  if (event.type === "message.updated") {
    const info = data["info"] as WireMessageInfo | undefined
    return info?.role === "assistant" && info.time?.completed !== undefined
  }
  return false
}

// What the session is doing, in the agent's own words.
//
// A turn that is being retried — rate limited, or a provider 5xx — looks exactly
// like a turn that is working unless someone says otherwise, indefinitely and
// with nothing to read. `session.status` says so; the retry part on the message
// says the same with the provider's own error attached.
//
// isTurnActive stays as the fallback. It is derived from events that are
// always published, so a server that does not publish session.status still
// gets a working spinner, just without the reason.
export interface SessionStatus {
  type: "idle" | "busy" | "retry"
  /** Retries only: which attempt this is. */
  attempt?: number
  /** Retries only: what the provider said. */
  message?: string
}

export function sessionStatus(event: SessionEvent): SessionStatus | undefined {
  const data = event.data ?? {}

  if (event.type === "session.status") {
    const status = data["status"] as { type?: string; attempt?: number; message?: string } | undefined
    switch (status?.type) {
      case "idle":
        return { type: "idle" }
      case "busy":
        return { type: "busy" }
      case "retry":
        return { type: "retry", attempt: status.attempt, message: status.message }
      default:
        return undefined
    }
  }

  // Deprecated alias the agent still emits alongside session.status.
  if (event.type === "session.idle") return { type: "idle" }

  // The durable record of a retry, which carries the provider's own error where
  // session.status carries a summary. Same state, better message.
  if (event.type === "message.part.updated") {
    const part = data["part"] as WirePart | undefined
    if (part?.type === "retry") {
      return {
        type: "retry",
        attempt: typeof part.attempt === "number" ? part.attempt : undefined,
        message: part.error == null ? undefined : errorText(part.error),
      }
    }
  }

  return undefined
}
